import React from 'react';
import PropTypes from 'prop-types';

import Select from '../../../../components/Select';
import Strings from '../../../../Strings';
import accessStyles from '../AccessSimulator/AccessSimulator.module.scss';
import { DraftField, toTextOrNull } from '../AthenaSimulator/fields';

const NPI_PATTERN = /^[12][0-9]{9}$/;

const TEXT_FIELDS = [
  ['first_name', 'nppesSimFirstName'],
  ['middle_name', 'nppesSimMiddleName'],
  ['last_name', 'nppesSimLastName'],
  ['name_prefix', 'nppesSimNamePrefix'],
  ['name_suffix', 'nppesSimNameSuffix'],
  ['credential', 'nppesSimCredential'],
  ['organization_name', 'nppesSimOrganizationName'],
];

// The registry's raw JSONB arrays, edited verbatim, like the Athena patient's
// extra fields.
const JSON_FIELDS = [
  ['taxonomies', 'nppesSimTaxonomies'],
  ['addresses', 'nppesSimAddresses'],
  ['practice_locations', 'nppesSimPracticeLocations'],
  ['endpoints', 'nppesSimEndpoints'],
];

const ENUMERATION_OPTIONS = [
  { value: 'NPI-1', label: Strings.nppesSimEnumIndividual },
  { value: 'NPI-2', label: Strings.nppesSimEnumOrganization },
];

const STATUS_OPTIONS = [
  { value: 'A', label: Strings.nppesSimStatusActive },
  { value: 'D', label: Strings.nppesSimStatusDeactivated },
];

const SEX_OPTIONS = [
  { value: null, label: Strings.nppesSimSexUnset },
  { value: 'M', label: 'M' },
  { value: 'F', label: 'F' },
];

const parseJSONArray = text => {
  try {
    const value = JSON.parse(text || '[]');
    return Array.isArray(value) ? value : undefined;
  } catch (error) {
    return undefined;
  }
};

// The draft keeps every field as the text the tester typed; the JSON arrays as
// pretty-printed JSON.
export const emptyProviderDraft = () => ({
  npi: '',
  enumeration_type: 'NPI-1',
  status: 'A',
  sex: null,
  last_updated: '',
  ...Object.fromEntries(TEXT_FIELDS.map(([field]) => [field, ''])),
  ...Object.fromEntries(JSON_FIELDS.map(([field]) => [field, '[]'])),
});

export const providerDraftFrom = provider => Object.fromEntries([
  ...Object.entries({ ...emptyProviderDraft(), ...provider }).map(([field, value]) => [
    field,
    JSON_FIELDS.some(([jsonField]) => jsonField === field)
      ? JSON.stringify(value ?? [], null, 2)
      : value ?? '',
  ]),
  ['sex', provider.sex ?? null],
]);

// Mirrors the backend validation so Save cannot produce a 400. The Luhn
// checksum is deliberately not checked: the real registry does not check it
// either, only the shape.
export const providerDraftIsValid = draft => (
  NPI_PATTERN.test(draft.npi.trim())
  && (draft.enumeration_type === 'NPI-2'
    ? !!draft.organization_name.trim()
    : !!draft.first_name.trim() && !!draft.last_name.trim())
  && (draft.last_updated === '' || /^\d{4}-\d{2}-\d{2}$/.test(draft.last_updated))
  && JSON_FIELDS.every(([field]) => parseJSONArray(draft[field]) !== undefined)
);

export const providerFromDraft = draft => ({
  npi: draft.npi.trim(),
  enumeration_type: draft.enumeration_type,
  status: draft.status,
  sex: draft.sex,
  last_updated: toTextOrNull(draft.last_updated),
  ...Object.fromEntries(TEXT_FIELDS.map(([field]) => [field, toTextOrNull(draft[field])])),
  ...Object.fromEntries(JSON_FIELDS.map(([field]) => [field, parseJSONArray(draft[field]) ?? []])),
});

/**
 * Add/edit form of a simulated NPI record: the modelled scalars plus the
 * registry's raw JSONB arrays (taxonomies, addresses, practice locations and
 * communication endpoints), served verbatim by the simulated Read API.
 */
function ProviderModal({ draft, setDraft, isSaving }) {
  const setField = (field, value) => setDraft({ ...draft, [field]: value });
  const isIndividual = draft.enumeration_type === 'NPI-1';
  return (
    <div className={accessStyles.patientModal}>
      <div className={accessStyles.patientModalIdentity}>
        <DraftField id="nppes-sim-provider-npi" label={Strings.nppesSimNpi} value={draft.npi} onChange={value => setField('npi', value)} disabled={isSaving} placeholder="1234567893" />
        <div className={accessStyles.patientModalField}>
          {Strings.nppesSimEnumerationType}
          <Select
            id="nppes-sim-provider-enumeration-type"
            data={ENUMERATION_OPTIONS}
            value={draft.enumeration_type}
            onChange={option => setField('enumeration_type', option?.value || 'NPI-1')}
            disabled={isSaving}
          />
        </div>
        <div className={accessStyles.patientModalField}>
          {Strings.nppesSimStatus}
          <Select
            id="nppes-sim-provider-status"
            data={STATUS_OPTIONS}
            value={draft.status}
            onChange={option => setField('status', option?.value || 'A')}
            disabled={isSaving}
          />
        </div>
        <div className={accessStyles.patientModalField}>
          {Strings.nppesSimSex}
          <Select
            id="nppes-sim-provider-sex"
            data={SEX_OPTIONS}
            value={draft.sex}
            onChange={option => setField('sex', option?.value ?? null)}
            disabled={isSaving}
          />
        </div>
        <DraftField id="nppes-sim-provider-last-updated" label={Strings.nppesSimLastUpdated} type="date" value={draft.last_updated} onChange={value => setField('last_updated', value)} disabled={isSaving} />
        {(isIndividual
          ? TEXT_FIELDS.filter(([field]) => field !== 'organization_name')
          : TEXT_FIELDS.filter(([field]) => field === 'organization_name')
        ).map(([field, label]) => (
          <DraftField key={field} id={`nppes-sim-provider-${field}`} label={Strings[label]} value={draft[field]} onChange={value => setField(field, value)} disabled={isSaving} />
        ))}
      </div>

      {JSON_FIELDS.map(([field, label]) => (
        <label key={field} className={accessStyles.scriptField} htmlFor={`nppes-sim-provider-${field}`}>
          {Strings[label]}
          <textarea
            id={`nppes-sim-provider-${field}`}
            rows={4}
            value={draft[field]}
            onChange={event => setField(field, event.target.value)}
            disabled={isSaving}
          />
        </label>
      ))}
      {JSON_FIELDS.some(([field]) => parseJSONArray(draft[field]) === undefined) && (
        <span className="validation-message">{Strings.nppesSimJSONArrayInvalid}</span>
      )}
      <p className={accessStyles.patientModalHint}>{Strings.nppesSimProviderHint}</p>
    </div>
  );
}

ProviderModal.propTypes = {
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

export default ProviderModal;
